import { useTranslation } from 'react-i18next'
import { Badge } from '@/ui/components/common/Badge'
import { Skeleton } from '@/ui/components/common/Skeleton'
import { useGetActiveInvestigation } from '@/application/services/investigation.service'
import { useGetLeadGroups } from '@/application/services/lead.service'
import { useGetAllFactsByLead } from '@/application/services/fact.service'
import { markdownToPlainExcerpt } from '@/ui/utils/markdown'

/**
 * Three-card section: active investigation, active lead, active fact.
 * Owns its data fetching.
 */
export function ActiveContextSection() {
  const { t } = useTranslation()
  const { data: activeInvestigation, isLoading: activeLoading } = useGetActiveInvestigation()
  const { data: leadGroups } = useGetLeadGroups()

  const leadGroupMap = new Map(
    (leadGroups ?? []).map((group) => [group.investigation.id, group.leads]),
  )

  const leads = activeInvestigation ? (leadGroupMap.get(activeInvestigation.id) ?? []) : []
  const activeLead = activeInvestigation?.activeLeadId
    ? leads.find((lead) => lead.id === activeInvestigation.activeLeadId)
    : [...leads].sort(
        (leadA, leadB) => new Date(leadA.createdAt).getTime() - new Date(leadB.createdAt).getTime(),
      )[0]

  const { data: leadFacts } = useGetAllFactsByLead(activeLead?.id ?? '')
  const activeFact = activeInvestigation?.activeFactId
    ? leadFacts?.find((fact) => fact.id === activeInvestigation.activeFactId)
    : undefined

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {/* Active Investigation */}
      <div className="border border-border rounded-xl p-4 bg-card">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {t('admin.live_active_investigation_label')}
        </p>
        {activeLoading ? (
          <Skeleton className="h-6 w-2/3" />
        ) : activeInvestigation ? (
          <div className="flex items-center gap-2">
            {activeInvestigation.imageUrl && (
              <img
                src={activeInvestigation.imageUrl}
                alt=""
                className="w-8 h-8 rounded object-cover shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-foreground truncate">{activeInvestigation.name}</p>
              <Badge variant="secondary" className="text-xs mt-0.5">
                {t('admin.live_active_badge')}
              </Badge>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            {t('admin.live_no_active_investigation')}
          </p>
        )}
      </div>

      {/* Active Lead */}
      <div className="border border-border rounded-xl p-4 bg-card">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {t('admin.live_active_lead_label')}
        </p>
        {activeLoading ? (
          <Skeleton className="h-6 w-2/3" />
        ) : activeLead ? (
          <div>
            <p className="font-semibold text-foreground truncate">
              {activeLead.title || markdownToPlainExcerpt(activeLead.description)}
            </p>
            <Badge variant="outline" className="text-xs mt-0.5 border-primary/30 text-primary">
              {t('admin.live_active_lead_badge')}
            </Badge>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">
            {activeInvestigation
              ? t('admin.live_no_active_lead')
              : t('admin.live_no_active_investigation')}
          </p>
        )}
      </div>

      {/* Active Fact */}
      <div className="border border-border rounded-xl p-4 bg-card">
        <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
          {t('admin.live_active_fact_label')}
        </p>
        {activeLoading ? (
          <Skeleton className="h-6 w-2/3" />
        ) : activeFact ? (
          <div className="flex items-center gap-2">
            {activeFact.imageUrl && (
              <img
                src={activeFact.imageUrl}
                alt=""
                className="w-8 h-8 rounded object-cover shrink-0"
              />
            )}
            <div className="min-w-0">
              <p className="font-semibold text-foreground truncate">{activeFact.title}</p>
              <Badge
                variant="secondary"
                className="text-xs mt-0.5 bg-purple-500/10 text-purple-600 border-purple-500/20"
              >
                {t('admin.live_active_fact_badge')}
              </Badge>
            </div>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground italic">{t('admin.live_no_active_fact')}</p>
        )}
      </div>
    </div>
  )
}
